import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CatalogItemFormDialog } from '../CatalogItemFormDialog';
import { CatalogSectionHeader } from '../CatalogSectionHeader';
import { ProductRelationsDialog } from './ProductRelationsDialog';
import { PRODUCTS_QUERY, SET_PRODUCT_STATUS_MUTATION } from './products.gql';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { CrudTable, type CrudColumn } from '@/shared/crud/CrudTable';
import { CrudToolbar } from '@/shared/crud/CrudToolbar';
import { useCrudList } from '@/shared/crud/useCrudList';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

interface Product {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category_id: string | null;
  unit_id: string | null;
  tax_rate_id: string | null;
  price_cents: string;
  cost_cents: string;
  stock_qty: string;
  stock_min: string;
  is_controlled: boolean;
  is_exception_handle: boolean;
  requires_current_weight: boolean;
  is_batch_tracked: boolean;
  is_perishable: boolean;
  is_vaccination_card_product: boolean;
  partial_kind: string;
  parent_product_id: string | null;
  parent_factor: string | null;
  barcode: string | null;
  branch_id: string | null;
  status: 'active' | 'inactive' | 'discontinued';
  created_at: string;
  updated_at: string;
}

interface ProductsResponse {
  products: {
    items: Product[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

export function ProductsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const list = useCrudList();
  const user = useAuthStore((s) => s.user);
  const can_write = user?.permissions.includes('catalog.products.write') ?? false;
  const is_admin = user?.roles.includes('administrator') ?? false;
  const [low_stock_only, setLowStockOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing_target, setEditingTarget] = useState<Product | null>(null);
  const [relations_target, setRelationsTarget] = useState<Product | null>(null);

  const { data, loading, refetch } = useQuery<ProductsResponse>(PRODUCTS_QUERY, {
    variables: {
      offset: list.offset,
      limit: list.limit,
      search: list.search || null,
      status_filter: list.status_filter,
      low_stock_only: low_stock_only || null,
    },
  });

  const [setStatus] = useMutation(SET_PRODUCT_STATUS_MUTATION);

  const onClickToggleStatus = async (row: Product): Promise<void> => {
    try {
      const result = await setStatus({
        variables: { id: row.id, status: row.status === 'active' ? 'inactive' : 'active' },
      });
      assertMutationSucceeded(result, 'update_product_status_failed');
      showToast({
        tone: 'success',
        title: t('alerts.update_success_title'),
        body: t('alerts.update_success_body'),
      });
      await refetch();
    } catch (err) {
      showToast({
        tone: 'error',
        title: t('alerts.update_error_title'),
        body: errorMessage(err),
      });
    }
  };

  const columns: CrudColumn<Product>[] = [
    {
      key: 'code',
      header_key: 'catalog.products.code',
      width: 'w-32',
      render: (r) => <span className="font-mono text-xs">{r.code}</span>,
    },
    {
      key: 'name',
      header_key: 'catalog.products.name',
      render: (r) => (
        <div>
          <div className="font-medium">{r.name}</div>
          {r.is_controlled ? (
            <Badge variant="destructive" className="mt-1">
              {t('catalog.products.controlled')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'price',
      header_key: 'catalog.products.price',
      align: 'right',
      width: 'w-32',
      render: (r) => formatCentsString(r.price_cents),
    },
    {
      key: 'stock',
      header_key: 'catalog.products.stock',
      align: 'right',
      width: 'w-32',
      render: (r) => {
        const qty = Number(r.stock_qty);
        const min = Number(r.stock_min);
        const low = !Number.isNaN(qty) && !Number.isNaN(min) && qty <= min;
        return (
          <span className={low ? 'text-destructive font-semibold' : undefined}>{r.stock_qty}</span>
        );
      },
    },
    {
      key: 'status',
      header_key: 'common.status',
      align: 'center',
      width: 'w-32',
      render: (r) => (
        <Badge variant={r.status === 'active' ? 'success' : 'muted'}>
          {t(`status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header_key: 'common.actions',
      align: 'right',
      width: 'w-56',
      render: (r) => (
        <div className="flex justify-end gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setRelationsTarget(r)}
          >
            {t('catalog.relations.open_action')}
          </Button>
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setEditingTarget(r)}
            >
              {t('common.edit')}
            </Button>
          ) : null}
          {can_write ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void onClickToggleStatus(r);
              }}
            >
              {r.status === 'active' ? t('common.deactivate') : t('common.activate')}
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <CatalogSectionHeader
        title={t('catalog.products.title')}
        subtitle={t('catalog.products.subtitle')}
      />

      <CrudTable<Product>
        rows={data?.products.items ?? []}
        columns={columns}
        loading={loading}
        page={data?.products.page ?? { total: 0, offset: 0, limit: list.limit, has_more: false }}
        onPageChange={list.setOffset}
        rowKey={(r) => r.id}
        empty_label_key="catalog.products.empty"
        toolbar={
          <CrudToolbar
            search={list.search}
            onSearchChange={list.setSearch}
            filters={
              <label className="text-muted-foreground flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={low_stock_only}
                  onChange={(e) => setLowStockOnly(e.target.checked)}
                />
                {t('catalog.products.low_stock_filter')}
              </label>
            }
            primary_action={
              is_admin ? (
                <Button type="button" onClick={() => setCreating(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  <span>{t('catalog.item_form.new_item')}</span>
                </Button>
              ) : undefined
            }
          />
        }
      />

      {creating ? (
        <CatalogItemFormDialog
          initial_kind="product"
          onClose={() => setCreating(false)}
          onSaved={async (kind) => {
            setCreating(false);
            showToast({
              tone: 'success',
              title: t('alerts.create_success_title'),
              body: t('alerts.create_success_body'),
            });
            if (kind === 'product') {
              await refetch();
            } else {
              navigate('/app/catalog/services');
            }
          }}
        />
      ) : null}

      {editing_target ? (
        <CatalogItemFormDialog
          initial_kind="product"
          product={editing_target}
          onClose={() => setEditingTarget(null)}
          onSaved={async () => {
            setEditingTarget(null);
            showToast({
              tone: 'success',
              title: t('alerts.update_success_title'),
              body: t('alerts.update_success_body'),
            });
            await refetch();
          }}
        />
      ) : null}

      {relations_target ? (
        <ProductRelationsDialog
          product_id={relations_target.id}
          product_name={relations_target.name}
          onClose={() => setRelationsTarget(null)}
        />
      ) : null}
    </div>
  );
}
